/**
 * HER PLACE: the microsite ProfileHub's main page, as a sheet the site's
 * login drawer opens (kit/drawer.ts). One sheet per place in the household:
 * the ticket stub and receipt, one row per decision (each opening its picker
 * here, the way the hub paged), when choices lock, and signing someone else
 * up while there are places to be had.
 *
 * Left behind on purpose: the hub's account, password and view-as panes (the
 * site's drawer has those) and its About you and household editors (the
 * profile page).
 *
 * Giving it away ends on its own page: her stub with "Offered" stamped across
 * it and who it is going to, so sending it feels like something happened.
 * The stamp stays on the stub, and on the floating ticket, until it moves.
 */
import { useState } from 'react';
import type { ChoiceChange, ChoicePerson, RegistrationInvoice } from '@/lib/types';
import type { ConferenceConfig, ConferencePhase, SeatCount } from '../lib/conference';
import { lockedCopy, registrationOpen } from '../lib/conference';
import type { Meals, TopicView } from '../lib/views';
import type { TransfersState } from '@/lib/api';
import { usePicks } from '../lib/usePicks';
import { Avatar } from './Avatar';
import { LunchPicker } from './LunchPicker';
import { LunchThumb } from './Lunch';
import { Sheet } from './Sheet';
import { SlotChoices } from './SlotChoices';
import { TicketCard } from './TicketCard';
import { useMeals } from './MealsProvider';

type Page = 'main' | 'lunch' | 'give' | 'sent' | `slot-${number}`;

export function PlaceSheet({
  open,
  onClose,
  config,
  person,
  imageUrl,
  invoice,
  faces,
  topics,
  phase,
  capacity,
  locked,
  choicesCloseAt,
  saveChoice,
  onRegister,
  signUpLabel,
  transfers,
  household = [],
  onOffer,
  onWithdraw,
  people = [],
  onPerson,
}: {
  open: boolean;
  onClose: () => void;
  config: ConferenceConfig;
  person: ChoicePerson | null;
  imageUrl: string | null;
  /** The registration invoice she is on, for the receipt. */
  invoice: RegistrationInvoice | null;
  /** Contact_ID -> face, for the others on her invoice. */
  faces: Map<number, string | null>;
  topics: TopicView[];
  phase: ConferencePhase;
  capacity?: SeatCount | null;
  locked: boolean;
  choicesCloseAt: string | null;
  saveChoice: (change: ChoiceChange) => Promise<string | null>;
  onRegister: () => void;
  signUpLabel: string;
  /** Whether the event takes transfers, and this household's offers (null: not loaded). */
  transfers?: TransfersState | null;
  /** Her household, for "give it to someone here". Already-registered people are left out. */
  household?: { contactId: number; name: string; imageUrl: string | null }[];
  /** Resolve with a problem in her words, or null when it is offered. */
  onOffer?: (offer: {
    eventParticipantId: number;
    toContactId?: number | null;
    toName?: string | null;
    toEmail?: string | null;
    note?: string | null;
  }) => Promise<string | null>;
  onWithdraw?: (transferId: number) => Promise<string | null>;
  /** Every place in the household, to switch between. */
  people?: { eventParticipantId: number; firstName: string }[];
  onPerson?: (eventParticipantId: number) => void;
}) {
  const meals: Meals = useMeals();
  const [page, setPage] = useState<Page>('main');
  const picks = usePicks(person, topics, meals, saveChoice);
  const [busy, setBusy] = useState(false);
  const [giveProblem, setGiveProblem] = useState<string | null>(null);
  if (!person) return null;
  const offer =
    transfers?.transfers.find(
      (x) =>
        x.eventParticipantId === person.eventParticipantId &&
        (x.status === 'offered' || x.status === 'approved')
    ) ?? null;
  const canGive = !!transfers?.allowed && transfers.open && person.canChange && !!onOffer;
  const stampText = offer ? (offer.status === 'approved' ? 'Moving' : 'Offered') : null;

  const name = `${person.firstName} ${person.lastName}`.trim();
  const editable =
    !locked && person.canChange && (phase === 'registration' || phase === 'preEvent');
  const seatsOffered = registrationOpen(phase, capacity);
  const close = () => {
    setPage('main');
    onClose();
  };

  // Her order as it stands now, from her picks: the invoice the page loaded
  // with is a snapshot, so after a change it named the old topic. The
  // registration disclaimer ('I Understand') is not an item; the microsite
  // hid it (hiddenOptionPriceIds) and so does this.
  const lines = [
    ...config.breakoutGroups.map((group) => {
      const topic = topics.find((t) => t.slug === picks.pickFor(group.slot));
      return {
        key: `slot-${group.slot}`,
        label: topic
          ? `${topic.shortTitle}${topic.speaker.name ? `: ${topic.speaker.name}` : ''}`
          : `${group.label}: no topic yet`,
      };
    }),
    ...(picks.lunch
      ? [
          {
            key: 'lunch',
            label: `Lunch: ${meals.options.find((o) => o.value === picks.lunch)?.label ?? picks.lunch}`,
          },
        ]
      : []),
  ];
  const party = (invoice?.people ?? []).map((p) => ({
    eventParticipantId: p.eventParticipantId,
    name: p.name,
    imageUrl: faces.get(p.contactId) ?? null,
    isMe: p.eventParticipantId === person.eventParticipantId,
  }));

  const lunchOption = picks.lunch ? meals.options.find((o) => o.value === picks.lunch) : undefined;
  const slotOpen = page.startsWith('slot-') ? Number(page.slice(5)) : null;
  const slotGroup = config.breakoutGroups.find((g) => g.slot === slotOpen);

  return (
    <Sheet open={open} onClose={close} title={name}>
      <div className="flex flex-col gap-7 p-6 pt-8 md:pt-10">
        {page !== 'main' && page !== 'sent' ? (
          <button
            type="button"
            onClick={() => setPage('main')}
            className="text-wm-sage-600 hover:text-wm-sage-950 self-start text-sm font-semibold transition-colors"
          >
            &larr; {person.firstName}
          </button>
        ) : null}

        {picks.problem ? (
          <p className="text-wm-plum text-sm font-semibold">{picks.problem}</p>
        ) : null}

        {page === 'main' ? (
          <>
            {people.length > 1 && onPerson ? (
              <div className="flex flex-wrap gap-2 pr-10">
                {people.map((p) => {
                  const on = p.eventParticipantId === person.eventParticipantId;
                  return (
                    <button
                      key={p.eventParticipantId}
                      type="button"
                      aria-pressed={on}
                      onClick={() => onPerson(p.eventParticipantId)}
                      className={`wm-lift rounded-full px-4 py-1.5 text-sm font-semibold ${
                        on ? 'bg-wm-sage-800 text-wm-cream' : 'wm-glass text-wm-sage-700'
                      }`}
                    >
                      {p.firstName}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="flex items-center gap-4 pr-8">
              <Avatar name={name} imageSrc={imageUrl} size={56} />
              <span className="min-w-0">
                <span className="wm-display block truncate text-3xl">{name}</span>
                <span className="wm-badge wm-badge-open mt-1.5">
                  {phase === 'recap' ? 'Attended' : 'Registered'}
                </span>
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <TicketCard
                config={config}
                name={name}
                lines={lines}
                total={invoice?.total ?? 0}
                paid={invoice?.paid ?? 0}
                party={party}
                stamp={stampText ? { text: stampText } : null}
              />
              {/* The ticket and handing it on are one thought (the microsite's
                  hub), so the way out sits under the stub. */}
              {offer ? (
                <div className="wm-card flex items-center justify-between gap-3 p-4">
                  <span className="min-w-0 text-sm">
                    <span className="block font-semibold">
                      {offer.status === 'approved' ? 'Moving to ' : 'Offered to '}
                      {offer.toName ?? 'whoever the team picks'}
                    </span>
                    <span className="text-wm-sage-500 block text-xs">
                      {offer.status === 'approved'
                        ? 'The team approved it. It moves in a few minutes.'
                        : 'The team will be in touch.'}
                    </span>
                  </span>
                  {offer.status === 'offered' && onWithdraw ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={async () => {
                        setBusy(true);
                        setGiveProblem(await onWithdraw(offer.id));
                        setBusy(false);
                      }}
                      className="wm-glass text-wm-sage-700 wm-lift shrink-0 rounded-full px-4 py-2 text-xs font-semibold"
                    >
                      {busy ? 'Taking it back…' : 'Keep my place'}
                    </button>
                  ) : null}
                </div>
              ) : canGive ? (
                <button
                  type="button"
                  onClick={() => setPage('give')}
                  className="text-wm-sage-600 hover:text-wm-sage-950 decoration-wm-sage-300 self-center text-sm font-semibold underline underline-offset-4 transition-colors"
                >
                  Cannot make it? Give your place away
                </button>
              ) : null}
            </div>

            <div className="flex flex-col">
              {config.breakoutGroups.map((group) => {
                const topic = topics.find((t) => t.slug === picks.pickFor(group.slot));
                return (
                  <Row
                    key={group.slot}
                    label={group.label.replace('Breakout Session', 'Breakout')}
                    value={topic?.shortTitle ?? null}
                    placeholder="No topic yet"
                    detail={topic?.room ?? undefined}
                    media={
                      topic ? (
                        <Avatar
                          name={topic.speaker.name}
                          imageSrc={topic.speaker.imageSrc}
                          size={40}
                        />
                      ) : undefined
                    }
                    onOpen={editable ? () => setPage(`slot-${group.slot}`) : undefined}
                  />
                );
              })}
              <Row
                label="Saturday lunch"
                value={lunchOption?.label ?? picks.lunch}
                placeholder="Not chosen"
                media={lunchOption ? <LunchThumb option={lunchOption} size={40} /> : undefined}
                onOpen={editable ? () => setPage('lunch') : undefined}
              />
              {locked ? (
                <p className="text-wm-sage-500 mt-3 text-xs">
                  {lockedCopy({ gatheringCloseAt: choicesCloseAt }, 'America/Detroit')}. Ask the
                  team if you need something moved.
                </p>
              ) : null}
            </div>

            {seatsOffered ? (
              <button
                type="button"
                onClick={() => {
                  close();
                  onRegister();
                }}
                className="bg-wm-marigold text-wm-sage-950 wm-lift w-full rounded-full px-6 py-3 text-sm font-semibold shadow"
              >
                {signUpLabel}
              </button>
            ) : null}
          </>
        ) : page === 'give' ? (
          <GivePage
            household={household}
            busy={busy}
            problem={giveProblem}
            onOffer={async (offer) => {
              if (!onOffer || !person) return;
              setBusy(true);
              const problem = await onOffer({
                ...offer,
                eventParticipantId: person.eventParticipantId,
              });
              setBusy(false);
              setGiveProblem(problem);
              if (!problem) setPage('sent');
            }}
          />
        ) : page === 'sent' ? (
          <>
            <TicketCard
              config={config}
              name={name}
              lines={[]}
              total={0}
              paid={0}
              stamp={{ text: stampText ?? 'Offered', land: true }}
            />
            <div className="wm-curtain-rise text-center">
              <p className="wm-kicker">Sent to the team</p>
              <h3 className="wm-display mt-1 text-3xl">
                {offer?.toName ? `Offered to ${offer.toName}` : 'Offered'}
              </h3>
            </div>
            <button
              type="button"
              onClick={close}
              className="bg-wm-marigold text-wm-sage-950 wm-lift w-full rounded-full px-6 py-3 text-sm font-semibold shadow"
            >
              Done
            </button>
          </>
        ) : page === 'lunch' ? (
          <>
            <div>
              <p className="wm-kicker">Saturday, 12 pm</p>
              <h3 className="wm-display mt-0.5 text-3xl">Lunch</h3>
            </div>
            <LunchPicker current={picks.lunch} onPick={(value) => void picks.setLunch(value)} />
          </>
        ) : slotOpen !== null && slotGroup ? (
          <>
            <div>
              <p className="wm-kicker">{slotGroup.agendaHint}</p>
              <h3 className="wm-display mt-0.5 text-3xl">{slotGroup.label}</h3>
            </div>
            <SlotChoices
              topics={topics}
              slot={slotOpen}
              pickedSlug={picks.pickFor(slotOpen)}
              phase={phase}
              editable={editable}
              guestCanRegister={false}
              onChoose={(topic) => void picks.setBreakout(slotOpen, topic)}
              onRegister={onRegister}
            />
          </>
        ) : null}
      </div>
    </Sheet>
  );
}

/** The hub's Row, verbatim but for the prefixed classes. */
function Row({
  label,
  value,
  placeholder,
  detail,
  media,
  onOpen,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  detail?: string;
  media?: React.ReactNode;
  onOpen?: () => void;
}) {
  const body = (
    <>
      <span className="flex min-w-0 items-center gap-3">
        {media ?? <span aria-hidden className="w-10 shrink-0" />}
        <span className="min-w-0">
          <span className="wm-kicker block">{label}</span>
          <span className={`block truncate text-sm ${value ? 'font-medium' : 'text-wm-sage-500'}`}>
            {value ?? placeholder}
          </span>
          {detail ? (
            <span className="text-wm-sage-500 block truncate text-xs">{detail}</span>
          ) : null}
        </span>
      </span>
      {onOpen ? (
        <svg
          aria-hidden
          viewBox="0 0 16 16"
          className="text-wm-sage-300 h-3.5 w-3.5 shrink-0 self-center"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 8h10M9 4l4 4-4 4" />
        </svg>
      ) : null}
    </>
  );
  const shell =
    'wm-dotted-rule flex w-full items-center justify-between gap-3 bg-bottom bg-repeat-x py-3.5 text-left last:bg-none';
  return onOpen ? (
    <button type="button" onClick={onOpen} className={`${shell} hover:text-wm-sage-950`}>
      {body}
    </button>
  ) : (
    <div className={shell}>{body}</div>
  );
}

/**
 * Giving a place away: someone in her household, someone she names, or
 * whoever the team picks. Every transfer is approved by the team in MP
 * (lib/events/transfers.ts in apps/api).
 */
function GivePage({
  household,
  busy,
  problem,
  onOffer,
}: {
  household: { contactId: number; name: string; imageUrl: string | null }[];
  busy: boolean;
  problem: string | null;
  onOffer: (offer: {
    toContactId?: number | null;
    toName?: string | null;
    toEmail?: string | null;
    note?: string | null;
  }) => void;
}) {
  const [to, setTo] = useState<number | 'named' | 'anyone' | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const ready =
    to === 'anyone' || typeof to === 'number' || (to === 'named' && name.trim().length > 1);
  const field =
    'border-wm-sage-200 bg-wm-petal focus:border-wm-sage-500 w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none';
  const choice = (on: boolean) =>
    `wm-lift flex w-full items-center gap-3 rounded-2xl border p-3 text-left ${
      on ? 'border-wm-sage-800 bg-wm-sage-100' : 'border-wm-sage-200 bg-wm-petal'
    }`;

  return (
    <>
      <div>
        <p className="wm-kicker">Cannot make it?</p>
        <h3 className="wm-display mt-0.5 text-3xl">Give your place away</h3>
        <p className="text-wm-sage-700 mt-2 text-sm">
          Registrations are not refundable, so your place goes to someone else. The team approves
          every transfer.
        </p>
      </div>

      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Who gets it">
        {household.map((m) => (
          <button
            key={m.contactId}
            type="button"
            role="radio"
            aria-checked={to === m.contactId}
            onClick={() => setTo(m.contactId)}
            className={choice(to === m.contactId)}
          >
            <Avatar name={m.name} imageSrc={m.imageUrl} size={36} />
            <span className="text-sm font-semibold">{m.name}</span>
          </button>
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={to === 'named'}
          onClick={() => setTo('named')}
          className={choice(to === 'named')}
        >
          <span className="bg-wm-cream flex h-9 w-9 items-center justify-center rounded-full text-lg">
            +
          </span>
          <span className="text-sm font-semibold">Someone else</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={to === 'anyone'}
          onClick={() => setTo('anyone')}
          className={choice(to === 'anyone')}
        >
          <span className="bg-wm-cream flex h-9 w-9 items-center justify-center rounded-full text-lg">
            ?
          </span>
          <span className="text-sm font-semibold">Whoever the team picks</span>
        </button>
      </div>

      {to === 'named' ? (
        <div className="flex flex-col gap-3">
          <label className="block">
            <span className="wm-kicker">Their name</span>
            <input
              className={`${field} mt-1.5`}
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="wm-kicker">Their email</span>
            <input
              className={`${field} mt-1.5`}
              type="email"
              value={email}
              maxLength={254}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </div>
      ) : null}

      {to !== null ? (
        <label className="block">
          <span className="wm-kicker">Anything the team should know</span>
          <textarea
            className={`${field} mt-1.5`}
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      ) : null}

      {problem ? <p className="text-wm-plum text-sm font-semibold">{problem}</p> : null}

      <button
        type="button"
        disabled={!ready || busy}
        onClick={() =>
          onOffer({
            toContactId: typeof to === 'number' ? to : null,
            toName: to === 'named' ? name : null,
            toEmail: to === 'named' ? email : null,
            note,
          })
        }
        className="bg-wm-marigold text-wm-sage-950 wm-lift w-full rounded-full px-6 py-3 text-sm font-semibold shadow disabled:opacity-50"
      >
        {busy ? 'Offering…' : 'Offer my place'}
      </button>
    </>
  );
}
